import { saveFilterState } from './filterState';
import { loadCollection } from '../../sections/collection/filteredCollection';
import SuggestionsSearch from '../overlay-search-nosto/suggestions-search';
import recommendationListVisibility from '../../sections/collection/recommendationListVisibility';
import { addSortingToURL } from './filterURL';

export default class Sorting {
  from_search;
  prefix;
  pendingSortCommit;
  lastCommittedDesktopSortValue;
  lastCommittedMobileSortValue;

  constructor(from_search = false) {
    this.from_search = from_search;
    this.prefix = this.from_search ? 'overlay-search-' : '';
    this.pendingSortCommit = false;
    this.lastCommittedDesktopSortValue = null;
    this.lastCommittedMobileSortValue = null;
    window.isSorting = false; // Radio can trigger 2 times due to focus
    this.initListeners();
  }

  /**
   * Select the sorting subnav and set focus to the first item for ADA purposes
   * 
   * @param {HTMLElement} subnav
   * @returns {void}
   */
  selectSortingSubnav = (subnav) => {
    if (!subnav) {
      return;
    }

    const parent = subnav.parentElement;
    if (!parent) {
      return;
    }

    const is_active = parent?.classList.contains('active');
    const sort_by = parent.querySelector(`[data-${this.prefix}sort-by]`);

    if (is_active) {
      parent.classList.remove('active');
      subnav.setAttribute('aria-expanded', 'false');
      sort_by?.setAttribute('aria-hidden', 'true');
      sort_by?.setAttribute('tabindex', '-1');
      this.setSortingInputsTabIndex(sort_by, -1);
      return;
    }

    parent.classList.add('active');
    subnav.setAttribute('aria-expanded', 'true');
    sort_by?.setAttribute('aria-hidden', 'false');
    sort_by?.setAttribute('tabindex', '0');
    this.setSortingInputsTabIndex(sort_by, 0);
    this.lastCommittedDesktopSortValue = this.getCheckedSortValue(sort_by, 'sortby');
    this.pendingSortCommit = false;

    this.setFocusToCheckedOrFirstSortingItem(sort_by);

    // Close all filter subnavs if open
    const filterSubnavs = document.querySelectorAll(`[data-${this.prefix}show-individual-filter]`);
    filterSubnavs.forEach((filterSubnav) => {
      filterSubnav.classList.remove('active');
      filterSubnav.setAttribute('aria-expanded', 'false');

      const panel = filterSubnav.querySelector(`[data-${this.prefix}filter-options-container]`);
      if (panel) {
        panel.setAttribute('aria-hidden', 'true');
        panel.setAttribute('tabindex', '-1');
      }
    });
  };

  /**
   * Set focus to the next sorting item in the list for ADA purposes
   * 
   * @param {HTMLElement} item
   * @returns {void}
   */
  setFocusToNextSortingItem = (item) => {
    const input = item?.matches?.('input[type="radio"]') ? item : item?.querySelector?.('input[type="radio"]');
    const listItem = input?.closest('li');
    const list = input?.closest('ul');
    if (!listItem || !list) {
      return;
    }

    const radios = Array.from(list.querySelectorAll('input[type="radio"]'));
    const element_index = radios.indexOf(input);

    if (element_index === -1) {
      return;
    }

    const nextIndex = element_index + 1 >= radios.length ? 0 : element_index + 1;
    this.focusSortingInput(radios[nextIndex]);
  };

  /**
   * Set focus to the previous sorting item in the list for ADA purposes
   * 
   * @param {HTMLElement} item
   * @returns {void}
   */
  setFocusToPreviousSortingItem = (item) => {
    const input = item?.matches?.('input[type="radio"]') ? item : item?.querySelector?.('input[type="radio"]');
    const listItem = input?.closest('li');
    const list = input?.closest('ul');
    if (!listItem || !list) {
      return;
    }

    const radios = Array.from(list.querySelectorAll('input[type="radio"]'));
    const element_index = radios.indexOf(input);

    if (element_index === -1) {
      return;
    }

    const previousIndex = element_index - 1 < 0 ? radios.length - 1 : element_index - 1;
    this.focusSortingInput(radios[previousIndex]);
  };

  /**
   * Set focus to the first sorting item in the list for ADA purposes
   * 
   * @param {HTMLElement} item
   * @returns {void}
   */
  setFocusToFirstSortingItem = (item) => {
    const firstInput = item?.querySelector('li input[type="radio"]');
    if (firstInput) {
      this.focusSortingInput(firstInput);
    }
  };

  setFocusToCheckedOrFirstSortingItem = (item) => {
    const checkedInput = item?.querySelector('li input[type="radio"]:checked');
    if (checkedInput) {
      this.focusSortingInput(checkedInput);
      return;
    }

    this.setFocusToFirstSortingItem(item);
  };

  focusAndSelectSortingInput = (input) => {
    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    input.focus();

    if (input.checked) {
      return;
    }

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  focusSortingInput = (input) => {
    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    input.focus();
  };

  setSortingInputsTabIndex = (container, tabIndexValue) => {
    if (!(container instanceof HTMLElement)) {
      return;
    }

    container.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.setAttribute('tabindex', String(tabIndexValue));
    });
  };

  getCheckedSortValue = (container, inputName) => {
    if (!(container instanceof HTMLElement)) {
      return null;
    }

    return container.querySelector(`input[name="${inputName}"]:checked`)?.value || null;
  };

  setCheckedSortValue = (container, inputName, value) => {
    if (!(container instanceof HTMLElement) || !value) {
      return;
    }

    const input = container.querySelector(`input[name="${inputName}"][value="${value}"]`);
    if (input instanceof HTMLInputElement) {
      input.checked = true;
    }
  };

  handleSortItemsClick = (e) => {
    const sortBy = e.target.closest(`[data-${this.prefix}sort-by]`);
    if (!sortBy) {
      return;
    }

    const clickableRadio = e.target.closest('input[type="radio"], .custom-radio');
    if (clickableRadio) {
      this.pendingSortCommit = true;
    }
  };

  /**
   * Handle keyboard events for the sort items for ADA purposes (Enter / Spacebar / Left / Right / Down)
   * 
   * @param {KeyboardEvent} e
   * @returns {void}
   */
  handleSortItemsKeydown = (e) => {
    const sortBy = e.target.closest(`[data-${this.prefix}sort-by]`);
    if (!sortBy) {
      return;
    }

    const input = e.target.closest('input[type="radio"]')
      || e.target.closest('.custom-radio')?.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    const key = e.key || '';
    const keyCode = e.which || e.keyCode;

    const isEnter = key === 'Enter' || keyCode === 13;
    const isSpace = key === ' ' || key === 'Spacebar' || keyCode === 32;
    const isLeft = key === 'ArrowLeft' || keyCode === 37;
    const isUp = key === 'ArrowUp' || keyCode === 38;
    const isRight = key === 'ArrowRight' || keyCode === 39;
    const isDown = key === 'ArrowDown' || keyCode === 40;
    const isHome = key === 'Home' || keyCode === 36;
    const isEnd = key === 'End' || keyCode === 35;
    const isTab = key === 'Tab' || keyCode === 9;

    if (isTab) {
      this.pendingSortCommit = false;
      e.preventDefault();
      e.stopPropagation();

      if (e.shiftKey) {
        this.setFocusToPreviousSortingItem(input);
      } else {
        this.setFocusToNextSortingItem(input);
      }
      return;
    }

    if (isEnter || isSpace) {
      this.pendingSortCommit = true;
      e.preventDefault();
      e.stopPropagation();
      this.focusAndSelectSortingInput(input);
      return;
    }

    if (isLeft || isUp) {
      this.pendingSortCommit = false;
      e.preventDefault();
      e.stopPropagation();
      this.setFocusToPreviousSortingItem(input);
      return;
    }

    if (isRight || isDown) {
      this.pendingSortCommit = false;
      e.preventDefault();
      e.stopPropagation();
      this.setFocusToNextSortingItem(input);
      return;
    }

    if (isHome) {
      this.pendingSortCommit = false;
      e.preventDefault();
      e.stopPropagation();
      this.setFocusToFirstSortingItem(sortBy);
      return;
    }

    if (isEnd) {
      this.pendingSortCommit = false;
      e.preventDefault();
      e.stopPropagation();
      const radios = sortBy.querySelectorAll('input[type="radio"]');
      const lastInput = radios[radios.length - 1];
      if (lastInput instanceof HTMLInputElement) {
        this.focusSortingInput(lastInput);
      }
    }
  };

  /**
   * Handle click events for the sort button
   * 
   * @param {MouseEvent} e
   * @returns {void}
   */
  handleSortButtonClick = (e) => {
    const item = e.target.closest(`[data-${this.prefix}show-desktop-sort-nav]`);
    if (!item) {
      return;
    }

    this.selectSortingSubnav(item);
  };

  /**
   * Handle keyboard events for the sort button for ADA purposes (Enter / Spacebar)
   * 
   * @param {KeyboardEvent} e
   * @returns {void}
   */
  handleSortButtonKeydown = (e) => {
    const item = e.target.closest(`[data-${this.prefix}show-desktop-sort-nav]`);
    if (!item) {
      return;
    }

    if (item.tagName.toLowerCase() === 'button') {
      return;
    }

    const key = e.which || e.keyCode;

    // Enter / spacebar
    if (key === 13 || key === 32) {
      item.click();
    }
  };

  /**
   * Update the sorting selection label based on the currently selected sorting option
   * Note that on page load, if the sorting option has been set already, it's init from applyFilterState() in filterState.js
   * 
   * @returns {void}
   */
  updateSortingSelection = ({ sortValue = null, sortLabel = null } = {}) => {
    const selectedSortValue = sortValue || (this.from_search
      ? window.theme.search?.sorting
      : window.theme.collections?.sorting);

    const sort_roots = this.from_search
      ? [
        '[data-overlay-search-show-desktop-sort-nav]',
        '[data-mobile-overlay-search-filter-nav-options]',
      ]
      : [
        '[data-show-desktop-sort-nav]',
        '[data-mobile-filter-nav-options]',
      ];

    sort_roots.forEach((root_selector) => {
      const root = document.querySelector(root_selector);
      if (!root) {
        return;
      }

      const selected_sort_elements = root.querySelectorAll(`[data-${this.prefix}selected-sort], [data-selected-sort]`);
      if (!selected_sort_elements.length) {
        return;
      }

      const selected_input = (selectedSortValue
        ? root.querySelector(`[data-${this.prefix}sort-by] input[value="${selectedSortValue}"]`)
        : null)
        || root.querySelector(`[data-${this.prefix}sort-by] input:checked`);

      const selected_sort_label = sortLabel
        || selected_input
          ?.closest('label')
          ?.querySelector('.custom-radio__label')
          ?.textContent
          ?.trim()
        || selectedSortValue;

      if (!selected_sort_label) {
        return;
      }

      selected_sort_elements.forEach((selected_sort_element) => {
        selected_sort_element.textContent = selected_sort_label;
      });
    });
  };

  restoreFocusToSortTrigger = () => {
    const selector = `[data-${this.prefix}show-desktop-sort-nav]`;

    const attemptFocus = (attempt = 0) => {
      const sortTrigger = document.querySelector(selector);
      if (sortTrigger instanceof HTMLElement) {
        sortTrigger.focus();
        return;
      }

      if (attempt >= 5) {
        return;
      }

      requestAnimationFrame(() => attemptFocus(attempt + 1));
    };

    requestAnimationFrame(() => attemptFocus());
  };

  /**
   * Close the sorting subnav after selection
   * 
   * @returns {void}
   */
  closeSortingSubnav = ({ restoreFocus = false } = {}) => {
    const sortButton = document.querySelector(`[data-${this.prefix}show-desktop-sort-nav]`);
    const subnav = sortButton?.parentElement;
    if (!subnav || !sortButton) {
      return;
    }

    subnav.classList.remove('active');
    sortButton.setAttribute('aria-expanded', 'false');

    const sortBy = subnav.querySelector(`[data-${this.prefix}sort-by]`);
    sortBy?.setAttribute('aria-hidden', 'true');
    sortBy?.setAttribute('tabindex', '-1');
    this.setSortingInputsTabIndex(sortBy, -1);

    if (restoreFocus) {
      this.restoreFocusToSortTrigger();
    }
  };

  /**
   * Sort change when coming from the PLP
   * 
   * @param {Event} e
   * @returns {Promise<void>}
   */
  handleCollectionSortChange = async (e) => {
    if (this.from_search) {
      return;
    }

    const target = e.target.closest('[data-sort-by]');
    if (!target) {
      return;
    }

    try {
      if (window.isSorting) {
        return;
      }
      window.isSorting = true;

      // const from_search = document.body.classList.contains('has-overlay-search');

      const mobile_parent = target.closest('[data-mobile-filter-nav-options]');
      const is_mobile = (mobile_parent !== null);
      const sortInputName = `sortby${(is_mobile) ? '-mobile' : ''}`;

      if (!this.pendingSortCommit) {
        const previousValue = is_mobile ? this.lastCommittedMobileSortValue : this.lastCommittedDesktopSortValue;
        this.setCheckedSortValue(target, sortInputName, previousValue);
        return;
      }

      // Get selected sorting value
      const checked_input = target.querySelector(`input[name="${sortInputName}"]:checked`);
      if (!checked_input) {
        console.log('No sorting option selected');
        return;
      }
      const selected_sort_label = checked_input
        ?.closest('label')
        ?.querySelector('.custom-radio__label')
        ?.textContent
        ?.trim();

      window.theme.collections.sorting = checked_input.value;
      if (is_mobile) {
        this.lastCommittedMobileSortValue = checked_input.value;
      } else {
        this.lastCommittedDesktopSortValue = checked_input.value;
      }

      // Activate the same option on desktop/mobile (doesn't trigger the click event!)
      if (is_mobile) {
        const desktop_input = document.querySelector(`#filters-panel-v3 [data-show-desktop-sort-nav]`)?.parentElement?.querySelector(`[data-sort-by] input[name="sortby"][value="${window.theme.collections.sorting}"]`);
        if (desktop_input) {
          desktop_input.checked = true;
          this.lastCommittedDesktopSortValue = window.theme.collections.sorting;
        }
      } else {
        const mobile_input = document.querySelector(`[data-mobile-filter-sorting] input[name="sortby-mobile"][value="${window.theme.collections.sorting}"]`);
        if (mobile_input) {
          mobile_input.checked = true;
          this.lastCommittedMobileSortValue = window.theme.collections.sorting;
        }

        // document.querySelectorAll('[data-mobile-filter-child="sort-by"] .mobile-filter__option').forEach(option => option.classList.remove('active'));
        // document.querySelector(`[data-mobile-filter-child="sort-by"] [data-option-value="${window.theme.collections.sorting}"]`)?.classList.add('active');
      }

      recommendationListVisibility();
      saveFilterState();
      addSortingToURL(window.theme.collections.sorting);
      await loadCollection(true);
      this.updateSortingSelection({
        sortValue: window.theme.collections.sorting,
        sortLabel: selected_sort_label,
      });

      // Close the sorting subnav after selection
      this.closeSortingSubnav({ restoreFocus: true });

      window.isSorting = false;
    } catch (error) {
      console.error('Error during sort change:', error);
    } finally {
      this.pendingSortCommit = false;
      window.isSorting = false;
    }
  };

  /**
   * Sort change when coming from the search overlay
   * 
   * @param {Event} e
   * @returns {Promise<void>}
   */
  handleSearchOverlaySortChange = async (e) => {
    if (!this.from_search) {
      return;
    }

    const target = e.target.closest('[data-overlay-search-sort-by]');
    if (!target) {
      return;
    }

    try {
      if (window.isSorting) {
        return;
      }
      window.isSorting = true;

      const mobile_parent = target.closest('[data-mobile-overlay-search-filter-nav-options]');
      const is_mobile = (mobile_parent !== null);
      const sortInputName = is_mobile ? 'sortby-overlay-search-mobile' : 'sortby';
      const fallbackMobileSortInputName = 'sortby-mobile';
      if (!this.pendingSortCommit) {
        const previousValue = is_mobile ? this.lastCommittedMobileSortValue : this.lastCommittedDesktopSortValue;
        this.setCheckedSortValue(target, sortInputName, previousValue);
        if (is_mobile) {
          this.setCheckedSortValue(target, fallbackMobileSortInputName, previousValue);
        }
        return;
      }

      const event_input = e.target instanceof HTMLInputElement && e.target.type === 'radio'
        ? e.target
        : null;

      const checked_input = event_input
        || target.querySelector(`input[name="${sortInputName}"]:checked`)
        || (is_mobile ? target.querySelector(`input[name="${fallbackMobileSortInputName}"]:checked`) : null)
        || target.querySelector('input[type="radio"]:checked');
      if (!checked_input) {
        console.log('No sorting option selected');
        return;
      }
      const selected_sort_label = checked_input
        ?.closest('label')
        ?.querySelector('.custom-radio__label')
        ?.textContent
        ?.trim();

      window.theme.search.page = 1;
      window.theme.search.sorting = checked_input.value;
      if (is_mobile) {
        this.lastCommittedMobileSortValue = checked_input.value;
      } else {
        this.lastCommittedDesktopSortValue = checked_input.value;
      }

      // Activate the same option on desktop/mobile (doesn't trigger the change event)
      if (is_mobile) {
        const desktop_input = document.querySelector(`[data-overlay-search-sort-by] input[name="sortby"][value="${window.theme.search.sorting}"]`);
        if (desktop_input) {
          desktop_input.checked = true;
          this.lastCommittedDesktopSortValue = window.theme.search.sorting;
        }
      } else {
        const mobile_input = document.querySelector(`[data-overlay-search-sort-by] input[name="sortby-overlay-search-mobile"][value="${window.theme.search.sorting}"]`);
        if (mobile_input) {
          mobile_input.checked = true;
          this.lastCommittedMobileSortValue = window.theme.search.sorting;
        }
      }

      const suggestionsSearch = new SuggestionsSearch();
      await suggestionsSearch.nostoSearch(true);
      this.updateSortingSelection({
        sortValue: window.theme.search.sorting,
        sortLabel: selected_sort_label,
      });

      this.closeSortingSubnav({ restoreFocus: true });
    } catch (error) {
      console.error('Error during overlay sort change:', error);
    } finally {
      this.pendingSortCommit = false;
      window.isSorting = false;
    }
  };

  /**
   * Initialize event listeners
   * 
   * @returns {void}
   */
  initListeners = () => {
    document.addEventListener('keydown', this.handleSortItemsKeydown, true);
    document.addEventListener('click', this.handleSortItemsClick, true);
    document.addEventListener('click', this.handleSortButtonClick);
    document.addEventListener('keydown', this.handleSortButtonKeydown);
    document.addEventListener('change', this.handleCollectionSortChange);
    document.addEventListener('change', this.handleSearchOverlaySortChange);
  };
}
